import { type ReactNode, useEffect, useEffectEvent, useId, useRef } from 'react';

// Modal: eigener, gestalteter Dialog statt Browser-Dialogen (alert/confirm/prompt). Fokus liegt im Dialog,
// Escape schließt (sofern erlaubt), der Hintergrund ist gesperrt.
export function Modal({
  title,
  children,
  actions,
  onClose,
  size = 'normal',
  dismissible = true,
  labelledBy,
}: {
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
  size?: 'normal' | 'wide';
  dismissible?: boolean;
  labelledBy?: string;
}) {
  const generated = useId();
  const titleId = labelledBy ?? `${generated}-title`;
  const box = useRef<HTMLDivElement>(null);
  // Escape mit den aktuellen Props – der Effekt läuft nur beim Öffnen (sonst spränge der Fokus bei jedem neuen onClose).
  const escape = useEffectEvent((e: KeyboardEvent) => {
    if (dismissible && onClose) {
      e.stopPropagation();
      onClose();
    }
  });

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const el = box.current;
    const first = el?.querySelector<HTMLElement>('[data-autofocus], input, textarea, select, button');
    (first ?? el)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') escape(e);
      if (e.key === 'Tab' && el) {
        // Fokus im Dialog halten.
        const items = [...el.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])')];
        if (items.length === 0) return;
        const [head, tail] = [items[0]!, items[items.length - 1]!];
        if (e.shiftKey && document.activeElement === head) {
          e.preventDefault();
          tail.focus();
        } else if (!e.shiftKey && document.activeElement === tail) {
          e.preventDefault();
          head.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.classList.remove('modal-open');
      previous?.focus?.();
    };
  }, []);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && dismissible && onClose?.()}>
      <div ref={box} className={`modal modal-${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <h2 id={titleId} className="modal-title">
          {title}
        </h2>
        <div className="modal-body">{children}</div>
        {actions && <div className="modal-actions">{actions}</div>}
      </div>
    </div>
  );
}
